import { MetaRenderColumn } from './styled';
import { Button, Grid, Box } from '@simplywallst/ui-core';
import PositiveNegativeValue from '@/components/PositiveNegativeValue';
import KeyMetric from '@/pages/CompanyReport/components/Overview/components/Header/components/KeyMetric';

import { toPercentage, toCurrencyTruncated } from '@/utilities/formatting';
import loadable from '@loadable/component';
import { shallowEqual, useSelector } from 'react-redux';
import type { LocationState } from 'redux-first-router';
import type { State as CompanyState } from '@/pages/CompanyReport/redux/interface';
import { format } from 'date-fns';
import { getUTCDate } from '@utilities/date';
import { useTracking } from 'react-tracking';
import { useState } from 'react';
import If from '@/components/If';
import { pathConstructor } from '@/hooks/useCompany/utils';
import { useTranslation } from '@/hooks/useTranslation';
import { useIsMobileScreen } from '@/hooks/useMedia/useMedia';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import {
  NarrativeKeyMetric,
  NarrativeKeyMetricChart,
} from '../NarrativeKeyMetric';

const AnalystsModal = loadable(
  () =>
    import(
      /* webpackPrefetch: true */ '@/pages/CompanyReport/components/AnalystsModal'
    )
);

type Selected =
  | { status: 'loading' }
  | {
      marketCapListing: number;
      marketCapListingFormatted: string;
      tradingItemCurrencyISO: string;
      status: 'success';
      return7D: number | null;
      return7DFormatted: string;
      return1Y: number | null;
      return1YFormatted: string;
      companyId: string;
      analystCount: number;
      lastAnalysisUpdateFormatted: string;
      sharePriceFormatted: string;
    };
function selector(state: {
  company: CompanyState;
  location: LocationState;
}): Selected {
  try {
    const path = pathConstructor(state.location);
    const companyId = state.company.urlToIdMapping[path];
    const summary = state.company.summary[companyId];
    const analysis = state.company.analysis[companyId];
    const formatting = state.company.formatting[companyId]; ////
    const tradingItemCurrencyISO = formatting.tradingItemCurrencyISO;
    if (companyId === '') {
      return {
        status: 'loading',
      };
    }
    const formatter = (value: number | null) =>
      value !== null ? toPercentage(value, 1) : 'n/a';
    const formatTimestamp = (timestamp: number): string => {
      if (typeof timestamp !== 'number' || timestamp === 0) return 'N/A';
      return format(getUTCDate(timestamp), 'dd MMM, yyyy');
    };
    return {
      marketCapListing: analysis.marketCapRawData.listing,
      marketCapListingFormatted: toCurrencyTruncated(
        analysis.marketCapRawData.listing,
        1,
        tradingItemCurrencyISO
      ),
      tradingItemCurrencyISO,
      status: 'success',
      return7D: analysis.return7D,
      return7DFormatted: formatter(analysis.return7D),
      return1Y: analysis.return1YrAbs,
      return1YFormatted: formatter(analysis.return1YrAbs),
      companyId,
      analystCount: summary.analystCount,
      lastAnalysisUpdateFormatted: formatTimestamp(summary.lastAnalysisUpdate),
      sharePriceFormatted: toCurrencyTruncated(
        analysis.sharePrice,
        2,
        tradingItemCurrencyISO
      ),
    };
  } catch {
    return { status: 'loading' };
  }
}

export function MetaData() {
  const selected = useSelector<
    Parameters<typeof selector>[0],
    ReturnType<typeof selector>
  >(selector, shallowEqual);
  const [showModal, setShowModal] = useState(false);
  const { trackEvent } = useTracking();
  const t = useTranslation();
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  if (selected.status === 'loading') return null;
  function handleToggleCompanyAnalystsModal() {
    if (selected.status === 'success') {
      trackEvent({
        action: 'click',
        subject: 'analystCount',
        analystCount: selected.analystCount,
      });
      setShowModal(true);
    }
  }

  return (
    <>
      <MetaRenderColumn alignSelf="flex-end" column>
        <Grid
          gap="0px"
          template="52px 52px 52px / repeat(4, 1fr)"
          templateLg="52px / repeat(4, min-content) 1fr 2fr 1fr 1fr"
        >
          <Grid>
            <KeyMetric
              type="Metric"
              testId="Last-Price"
              title={t('page.companyReport.header.metadata.lastPrice')}
              metric={selected.sharePriceFormatted}
            />
          </Grid>
          <Grid>
            <KeyMetric
              type="Metric"
              testId="Market-Cap"
              title={t('page.companyReport.header.metadata.marketCap')}
              metric={selected.marketCapListingFormatted}
            />
          </Grid>
          {isMobileScreen && (
            <Grid column="span 2">
              <NarrativeKeyMetric companyId={selected.companyId} />
            </Grid>
          )}
          <Grid
            column="span 1"
            columnLg="span 2"
            templateColumns="auto auto"
            justifyContent="start"
            alignItems="start"
            gap="16px"
            gapLg="0px"
          >
            <KeyMetric
              type="Metric"
              testId="7D"
              title="7D"
              metric={
                <PositiveNegativeValue value={selected.return7D}>
                  {selected.return7DFormatted}
                </PositiveNegativeValue>
              }
            />
            <KeyMetric
              type="Metric"
              testId="1Y"
              title="1Y"
              metric={
                <PositiveNegativeValue value={selected.return1Y}>
                  {selected.return1YFormatted}
                </PositiveNegativeValue>
              }
            />
          </Grid>
          {isMobileScreen && (
            <Grid column="span 3">
              <Box marginLeft="x2">
                <NarrativeKeyMetricChart
                  companyId={selected.companyId}
                  currencyISO={selected.tradingItemCurrencyISO}
                />
              </Box>
            </Grid>
          )}
          {!isMobileScreen && (
            <>
              <Grid column="span 2" templateColumns="min-content auto">
                <NarrativeKeyMetric companyId={selected.companyId} />
                <Box marginRight="x3">
                  <NarrativeKeyMetricChart
                    companyId={selected.companyId}
                    currencyISO={selected.tradingItemCurrencyISO}
                  />
                </Box>
              </Grid>
            </>
          )}
          {typeof selected.lastAnalysisUpdateFormatted !== 'undefined' ? (
            <Grid column="span 1">
              <KeyMetric
                type="Updated"
                testId="Updated"
                title={t('page.companyReport.header.metadata.updated')}
                metric={selected.lastAnalysisUpdateFormatted}
              />
            </Grid>
          ) : null}
          <Grid column="span 3" columnLg="span 1">
            <KeyMetric
              type="Data"
              testId="Data"
              title={t('page.companyReport.header.metadata.data')}
              metric={
                <>
                  <span>
                    {t('page.companyReport.header.metadata.companyFinancials')}{' '}
                  </span>
                  {selected.analystCount > 0 && (
                    <>
                      <span>{'+ '}</span>
                      <Button
                        styleType="softLink"
                        data-cy-id="analyst-modal-button"
                        onClick={handleToggleCompanyAnalystsModal}
                      >
                        {t('page.companyReport.header.metadata.analyst', {
                          count: selected.analystCount,
                        })}
                      </Button>
                    </>
                  )}
                </>
              }
            />
          </Grid>
        </Grid>
      </MetaRenderColumn>
      <If match={showModal}>
        {() => (
          <AnalystsModal
            companyId={selected.companyId}
            closeModal={() => setShowModal(false)}
          />
        )}
      </If>
    </>
  );
}
